// From draft to publication (#133): test a draft's versions in the wind
// tunnel, ask for and record an approval, send the approved version to Brand
// Publisher, and see what was published from it and its retrieval re-check.
// Every rule is the server's (app/services/content_studio/gate.py); the
// publish button is disabled until the server says the gate is clear.

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { FlaskConical, Send, ShieldCheck } from 'lucide-react';
import {
  approve, getPublishState, planDraftTest, requestApproval, sendToPublisher, testDraft, withdrawApproval,
} from '@/api/content';
import type { Approval, Draft, DraftTest, Publication, PublishState, TestBelief, TestPlan } from '@/api/content';

const MUTED = 'var(--muted-foreground)';
const DESTINATIONS = [
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'hashnode', label: 'Hashnode' },
  { id: 'wordpress', label: 'WordPress (as a draft)' },
];
const VERDICT_COLOR: Record<string, string> = {
  PASS: 'var(--success-text)', CONDITIONAL: 'var(--warning-text)', FAIL: 'var(--infrared-coral)',
};
// The wind tunnel's shading of a difference from control (analysis._shade):
// past the warn line, or (_strong) past the fail line.
const SHADE_COLOR: Record<string, string> = {
  loss: 'var(--warning-text)', loss_strong: 'var(--infrared-coral)',
  gain: 'var(--success-text)', gain_strong: 'var(--success-text)',
};
const input = {
  fontSize: 'var(--text-xs)', height: '28px', padding: '0 8px', borderRadius: '6px', border: '1px solid var(--border)',
  backgroundColor: 'var(--background)', color: 'var(--foreground)', boxSizing: 'border-box' as const,
};

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: '12px' }}>
      <div className="ds-act-section__label" style={{ marginBottom: '6px' }}>{title}</div>
      {children}
    </div>
  );
}

function fmt(value: number | null | undefined, scale: string): string {
  if (value == null) return '–';
  return scale === 'rate' ? `${Math.round(value * 100)}%` : value.toFixed(2);
}

function delta(value: number | null | undefined, scale: string): string {
  if (value == null) return '–';
  const sign = value > 0 ? '+' : '';
  return scale === 'rate' ? `${sign}${value.toFixed(1)}pp` : `${sign}${value.toFixed(2)}`;
}

/** Scores first: the per-belief deltas against control, then the verdict as
 *  the wind tunnel's opinion, with the rule it applied. */
function TestResult({ test }: { test: DraftTest }) {
  const r = test.result_json || {};
  const rows: TestBelief[] = [...(r.beliefs || []), ...(r.visibility || [])];
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '8px 10px', fontSize: 'var(--text-xs)' }}>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <b>Version {test.version_number}</b>
        <span style={{ color: MUTED }}>
          run #{test.wind_tunnel_run_id} · {test.scope === 'belief'
            ? `${test.question_ids.length} question${test.question_ids.length === 1 ? '' : 's'} mapped to ${test.belief_ids.join(', ')}`
            : `full battery (${test.question_ids.length} questions)`}
          {test.question_types.length > 0 && ` · ${test.question_types.join(', ')}`}
          {' '}· asked by {test.requested_by}
        </span>
        {test.status === 'pending' && <span style={{ color: MUTED }}>Running…</span>}
        {test.status !== 'pending' && test.status !== 'completed' && (
          <span style={{ color: 'var(--warning-text)' }}>Run {test.status}{r.stop_reason ? ` (${r.stop_reason})` : ''}: not a result</span>
        )}
        {test.status === 'completed' && !test.complete && (
          <span style={{ color: 'var(--warning-text)' }}>Incomplete: not every call was answered and judged</span>
        )}
      </div>
      {rows.length > 0 && (
        <table style={{ width: '100%', marginTop: '6px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: MUTED, textAlign: 'left' }}>
              <th style={{ fontWeight: 500 }}>Belief / row</th><th style={{ fontWeight: 500 }}>Control</th>
              <th style={{ fontWeight: 500 }}>This version</th><th style={{ fontWeight: 500 }}>Difference</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(b => (
              <tr key={`${b.scale}-${b.id}`} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '3px 0', fontWeight: b.targeted ? 600 : 400 }}>
                  {b.label}{b.targeted ? ' (targeted)' : ''}
                </td>
                <td>{fmt(b.control, b.scale)}</td>
                <td>{fmt(b.value, b.scale)}</td>
                <td style={{ color: SHADE_COLOR[b.shade || ''] || 'inherit' }}>
                  {delta(b.delta, b.scale)}
                  {b.ci95 ? <span style={{ color: MUTED }}> ({b.ci95[0].toFixed(2)} to {b.ci95[1].toFixed(2)})</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {test.verdict && (
        <p style={{ marginTop: '6px' }}>
          Wind tunnel's opinion: <b style={{ color: VERDICT_COLOR[test.verdict] }}>{test.verdict}</b>
          {r.read ? ` · ${r.read}` : ''}
          {test.verdict_rule && <span style={{ display: 'block', color: MUTED, marginTop: '2px' }}>Rule: {test.verdict_rule}</span>}
        </p>
      )}
    </div>
  );
}

function ApprovalLine({ a }: { a: Approval }) {
  return (
    <li>
      Version {a.version_number}: asked by {a.requested_by} {new Date(a.requested_at).toLocaleString()}
      {a.request_note ? ` ("${a.request_note}")` : ''}
      {a.status === 'approved' && <> · <b style={{ color: 'var(--success-text)' }}>{a.kind_label}</b> by {a.approved_by}
        {' '}{a.approved_at ? new Date(a.approved_at).toLocaleString() : ''}{a.reason ? ` · reason: ${a.reason}` : ''}</>}
      {a.status === 'withdrawn' && <> · withdrawn by {a.withdrawn_by}{a.reason ? ` (${a.reason})` : ''}</>}
      {a.status === 'requested' && <> · waiting for approval</>}
    </li>
  );
}

export function PublicationLine({ p }: { p: Publication }) {
  const lanes = p.recheck_result?.lanes || [];
  return (
    <li style={{ marginBottom: '4px' }}>
      <b>{p.destination}</b> {new Date(p.published_at).toLocaleString()}
      {p.url && <> · <a href={p.url} target="_blank" rel="noreferrer">{p.url}</a></>}
      {' '}· approved by {p.approved_by} ({p.approval_kind_label})
      {p.wind_tunnel_verdict && <> · wind tunnel {p.wind_tunnel_verdict}</>}
      {p.belief_ids.length > 0 && <> · targets {p.belief_ids.join(', ')}</>}
      <span style={{ display: 'block', color: MUTED }}>
        Retrieval re-check: {p.recheck_status.replace('_', ' ')}
        {p.recheck_status === 'scheduled' && p.recheck_due_at ? `, due ${new Date(p.recheck_due_at).toLocaleDateString()}` : ''}
        {p.recheck_note ? ` · ${p.recheck_note}` : ''}
        {lanes.map((l: Record<string, any>) => (
          <span key={l.lane}> · {l.lane}: {l.rate == null ? '–' : `${Math.round(l.rate * 100)}% named`}
            {l.delta_pp_vs_parent != null ? ` (${l.delta_pp_vs_parent > 0 ? '+' : ''}${l.delta_pp_vs_parent}pp vs before publishing)` : ''}</span>
        ))}
      </span>
    </li>
  );
}

export function PublishPanel({ customerId, draft, isAdmin }: { customerId: number; draft: Draft; isAdmin: boolean }) {
  const [state, setState] = useState<PublishState | null>(null);
  const [busy, setBusy] = useState(false);
  const latest = draft.latest.version_number;
  const [versions, setVersions] = useState<number[]>([latest]);
  const [scope, setScope] = useState<'belief' | 'battery'>(draft.brief.brief?.belief ? 'belief' : 'battery');
  const [plan, setPlan] = useState<TestPlan | null>(null);
  const [cap, setCap] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [confirmSelf, setConfirmSelf] = useState(false);
  const [destinations, setDestinations] = useState<string[]>(['linkedin']);

  const load = useCallback(() => getPublishState(customerId, draft.id).then(setState)
    .catch(e => toast.error(e?.message || 'Could not load the publish state')), [customerId, draft.id]);
  useEffect(() => { load(); }, [load, latest]);
  useEffect(() => { setVersions([latest]); setPlan(null); }, [latest]);

  // A test is running: look again every 20 seconds until it settles.
  const running = (state?.tests || []).some(t => t.status === 'pending');
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(load, 20000);
    return () => window.clearInterval(timer);
  }, [running, load]);

  if (!state) return <p style={{ fontSize: 'var(--text-xs)', color: MUTED }}>Loading the way to publish…</p>;
  if (!state.available) {
    return <p style={{ fontSize: 'var(--text-xs)', color: MUTED }}>Testing and approving are not available yet: apply {state.migration} first.</p>;
  }

  const run = async (fn: () => Promise<PublishState | void>, done: string) => {
    setBusy(true);
    try {
      const next = await fn();
      if (next) setState(next); else await load();
      toast.success(done);
    } catch (e: any) {
      toast.error(e?.message || 'Could not do that');
    } finally {
      setBusy(false);
    }
  };

  const gate = state.gate!;
  const open = (state.approvals || []).find(a => a.status === 'requested' && a.version_number === latest);
  const approved = gate.approval;
  const toggle = (list: string[] | number[], value: any, set: (v: any) => void) =>
    set(list.includes(value as never) ? list.filter((x: any) => x !== value) : [...list, value]);

  const estimate = () => run(async () => {
    const p = await planDraftTest(customerId, draft.id, { versions, scope });
    setPlan(p);
    setCap(String(p.estimate.suggested_cap_usd));
  }, 'Estimated');

  return (
    <div style={{ marginTop: '14px', paddingTop: '10px', borderTop: '1px solid var(--border)' }}>
      <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
        <ShieldCheck size={15} /> Test, approve, publish
      </p>
      <p style={{ fontSize: 'var(--text-xs)', marginTop: '4px', color: gate.ready ? 'var(--success-text)' : 'var(--warning-text)' }}>
        {gate.ready
          ? `Version ${gate.version_number} is tested and approved: it may be published.`
          : `Version ${gate.version_number} cannot be published yet: ${gate.blockers.map(b => b.message).join('; ')}.`}
      </p>

      <Block title="1. Wind tunnel">
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', fontSize: 'var(--text-xs)' }}>
          <span style={{ color: MUTED }}>Versions side by side:</span>
          {draft.versions.slice(-6).map(x => (
            <label key={x.version_number} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <input type="checkbox" checked={versions.includes(x.version_number)}
                onChange={() => { toggle(versions, x.version_number, setVersions); setPlan(null); }} /> v{x.version_number}
            </label>
          ))}
          <select value={scope} onChange={e => { setScope(e.target.value as 'belief' | 'battery'); setPlan(null); }} style={input}>
            <option value="belief" disabled={!draft.brief.brief?.belief}>
              Questions mapped to {draft.brief.brief?.belief?.name || 'the targeted belief'}
            </option>
            <option value="battery">Full battery</option>
          </select>
          <button className="ds-act-btn" disabled={busy || versions.length === 0 || versions.length > 4} onClick={estimate}>Estimate</button>
        </div>
        {plan && (
          <div style={{ marginTop: '8px', fontSize: 'var(--text-xs)', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: MUTED }}>
              {plan.questions} question{plan.questions === 1 ? '' : 's'} ({plan.question_types.join(', ') || 'no type'}) ×{' '}
              {plan.passes} passes × {plan.lanes.map(l => l.label || l.lane).join(', ')}: {plan.estimate.calls} calls, about
              ${plan.estimate.total_usd.toFixed(2)}.
            </span>
            <label>Spend cap $ <input value={cap} onChange={e => setCap(e.target.value)} style={{ ...input, width: '70px' }} /></label>
            <button className="ds-act-btn ds-act-btn--primary" disabled={busy || !(Number(cap) > 0)}
              onClick={() => run(async () => (await testDraft(customerId, draft.id, {
                versions, scope, spend_cap_usd: Number(cap),
              })).state, 'Sent to the wind tunnel; the result appears here when the run finishes')}>
              <FlaskConical size={13} /> Test {versions.length > 1 ? `${versions.length} versions` : `v${versions[0]}`}
            </button>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
          {(state.tests || []).map(t => <TestResult key={t.id} test={t} />)}
          {(state.tests || []).length === 0 && <p style={{ fontSize: 'var(--text-xs)', color: MUTED }}>Not tested yet.</p>}
        </div>
      </Block>

      <Block title="2. Approval">
        {(state.approvals || []).length > 0 && (
          <ul style={{ margin: '0 0 6px', paddingLeft: '18px', fontSize: 'var(--text-xs)' }}>
            {(state.approvals || []).map(a => <ApprovalLine key={a.id} a={a} />)}
          </ul>
        )}
        {!approved && !open && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Note for the approver (optional)"
              style={{ ...input, flex: 1 }} />
            <button className="ds-act-btn" disabled={busy}
              onClick={() => run(() => requestApproval(customerId, draft.id, latest, note), `Approval of version ${latest} requested`)}>
              Ask for approval of v{latest}
            </button>
          </div>
        )}
        {open && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--text-xs)' }}>
            {state.can_self_approve && (
              <label style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input type="checkbox" checked={confirmSelf} onChange={e => setConfirmSelf(e.target.checked)} />
                I asked for this and I am the only person on this client's team: I confirm a second time
              </label>
            )}
            {isAdmin && (
              <input value={reason} onChange={e => setReason(e.target.value)}
                placeholder="Admin override reason (only to approve your own request)" style={{ ...input, flex: 1, minWidth: '220px' }} />
            )}
            <button className="ds-act-btn ds-act-btn--primary" disabled={busy || !gate.test}
              title={gate.test ? 'Approve this version for publishing' : 'Approve once the wind tunnel result is in'}
              onClick={() => run(() => approve(customerId, open.id, { confirm_self: confirmSelf, reason }), `Version ${latest} approved`)}>
              Approve v{latest}
            </button>
            <button className="ds-act-btn" disabled={busy}
              onClick={() => run(() => withdrawApproval(customerId, open.id), 'Request withdrawn')}>Withdraw</button>
          </div>
        )}
      </Block>

      <Block title="3. Publish">
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--text-xs)' }}>
          {DESTINATIONS.map(d => (
            <label key={d.id} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <input type="checkbox" checked={destinations.includes(d.id)} onChange={() => toggle(destinations, d.id, setDestinations)} /> {d.label}
            </label>
          ))}
          <button className={gate.ready ? 'ds-act-btn ds-act-btn--primary' : 'ds-act-btn'}
            disabled={busy || !gate.ready || destinations.length === 0}
            title={gate.ready ? 'Opens as a Brand Publisher draft of yours, linked to this version'
                              : gate.blockers.map(b => b.message).join('; ')}
            onClick={() => run(async () => {
              const r = await sendToPublisher(customerId, draft.id, latest, destinations);
              toast.message(`Brand Publisher draft #${r.publishing_draft_id} is ready: publish it from Brand Publisher.`);
            }, `Version ${latest} sent to Brand Publisher`)}>
            <Send size={13} /> Publish v{latest} via Brand Publisher
          </button>
        </div>
        {(state.publications || []).length > 0 && (
          <ul style={{ margin: '8px 0 0', paddingLeft: '18px', fontSize: 'var(--text-xs)' }}>
            {(state.publications || []).map(p => <PublicationLine key={p.id} p={p} />)}
          </ul>
        )}
      </Block>

      {isAdmin && (state.log || []).length > 0 && (
        <details style={{ marginTop: '10px', fontSize: 'var(--text-xs)' }}>
          <summary style={{ cursor: 'pointer', color: MUTED }}>Log ({(state.log || []).length})</summary>
          <ul style={{ margin: '6px 0 0', paddingLeft: '18px', color: MUTED }}>
            {(state.log || []).map(e => (
              <li key={e.id}>{new Date(e.at).toLocaleString()} · {e.actor} · {e.kind.replace(/_/g, ' ')}
                {e.version_number != null ? ` · v${e.version_number}` : ''}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
